export class EnhancedImage {
  static get toolbox() {
    return {
      title: "Image",
      icon: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" stroke-width="1.5"/>
        <circle cx="6" cy="6" r="1" fill="currentColor"/>
        <path d="m9 11-1-1-2 2-2-2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>`,
    };
  }

  constructor({ data, config, api }) {
    this.api = api;
    this.config = config;
    this.data = {
      url: data.url || "",
      caption: data.caption || "",
      alignment: data.alignment || "center",
      width: data.width || 100,
    };
    this.wrapper = null;
    this.image = null;
  }

  render() {
    this.wrapper = document.createElement("div");
    this.wrapper.style.cssText = `
      position: relative;
      text-align: ${this.data.alignment};
      margin: 20px 0;
    `;

    if (this.data.url) {
      this.createImage();
    } else {
      this.createUploadArea();
    }

    return this.wrapper;
  }

  createImage() {
    const container = document.createElement("div");
    container.style.cssText = `
      display: inline-block;
      position: relative;
      max-width: 100%;
    `;

    this.image = document.createElement("img");
    this.image.src = this.data.url;
    this.image.style.cssText = `
      width: ${this.data.width}%;
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    `;

    const caption = document.createElement("div");
    caption.contentEditable = true;
    caption.innerHTML = this.data.caption;
    caption.style.cssText = `
      margin-top: 8px;
      font-size: 14px;
      color: #666;
      font-style: italic;
      text-align: center;
      outline: none;
    `;
    caption.setAttribute("data-placeholder", "Enter caption...");

    caption.addEventListener("input", () => {
      this.data.caption = caption.innerHTML;
    });

    container.appendChild(this.image);
    container.appendChild(caption);
    this.wrapper.appendChild(container);
  }

  createUploadArea() {
    const uploadArea = document.createElement("div");
    uploadArea.style.cssText = `
      border: 2px dashed #ddd;
      border-radius: 8px;
      padding: 40px;
      text-align: center;
      cursor: pointer;
      transition: border-color 0.3s;
    `;
    uploadArea.innerHTML = `
      <div style="font-size: 48px; color: #ddd; margin-bottom: 16px;">📷</div>
      <div style="font-size: 16px; color: #666;">Click to upload image</div>
      <div style="font-size: 14px; color: #999; margin-top: 8px;">PNG, JPG, GIF up to 10MB</div>
    `;

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    fileInput.style.display = "none";

    fileInput.addEventListener("change", (e) => {
      const file = e.target.files[0];
      if (file) {
        this.uploadImage(file);
      }
    });

    uploadArea.addEventListener("click", () => fileInput.click());
    this.wrapper.appendChild(uploadArea);
    this.wrapper.appendChild(fileInput);
  }

  uploadImage(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      this.data.url = e.target.result;
      this.wrapper.innerHTML = "";
      this.createImage();
    };
    reader.readAsDataURL(file);
  }

  renderSettings() {
    const wrapper = document.createElement("div");
    wrapper.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 8px;
      min-width: 200px;
    `;

    // Alignment controls
    const alignmentDiv = document.createElement("div");
    alignmentDiv.innerHTML =
      '<div style="font-size: 12px; margin-bottom: 4px; font-weight: bold;">Alignment</div>';

    const alignments = [
      { value: "left", icon: "⬅️", label: "Left" },
      { value: "center", icon: "↔️", label: "Center" },
      { value: "right", icon: "➡️", label: "Right" },
    ];

    const alignmentButtons = document.createElement("div");
    alignmentButtons.style.cssText = "display: flex; gap: 4px;";

    alignments.forEach((align) => {
      const btn = document.createElement("button");
      btn.innerHTML = align.icon;
      btn.title = align.label;
      btn.style.cssText = `
        padding: 6px 8px;
        border: 1px solid ${
          this.data.alignment === align.value ? "#007bff" : "#ddd"
        };
        background: ${this.data.alignment === align.value ? "#007bff" : "#fff"};
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
      `;
      btn.addEventListener("click", () => {
        this.data.alignment = align.value;
        this.wrapper.style.textAlign = align.value;
      });
      alignmentButtons.appendChild(btn);
    });

    alignmentDiv.appendChild(alignmentButtons);

    // Width control
    const widthDiv = document.createElement("div");
    widthDiv.innerHTML =
      '<div style="font-size: 12px; margin-bottom: 4px; font-weight: bold;">Width</div>';

    const widthSlider = document.createElement("input");
    widthSlider.type = "range";
    widthSlider.min = "25";
    widthSlider.max = "100";
    widthSlider.value = this.data.width;
    widthSlider.style.cssText = "width: 100%;";

    const widthLabel = document.createElement("div");
    widthLabel.style.cssText = "font-size: 11px; color: #666; margin-top: 2px;";
    widthLabel.textContent = `${this.data.width}%`;

    widthSlider.addEventListener("input", () => {
      this.data.width = widthSlider.value;
      widthLabel.textContent = `${this.data.width}%`;
      if (this.image) {
        this.image.style.width = `${this.data.width}%`;
      }
    });

    widthDiv.appendChild(widthSlider);
    widthDiv.appendChild(widthLabel);

    wrapper.appendChild(alignmentDiv);
    wrapper.appendChild(widthDiv);

    return wrapper;
  }

  save() {
    return {
      url: this.data.url,
      caption: this.data.caption,
      alignment: this.data.alignment,
      width: this.data.width,
    };
  }

  static get pasteConfig() {
    return {
      tags: ["IMG"],
    };
  }
}

// Text Alignment Tool Component
export class AlignmentTool {
  static get toolbox() {
    return {
      title: "Alignment",
      icon: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M2 4h12M2 6h8M2 8h12M2 10h6" stroke="currentColor" stroke-width="1.5"/>
      </svg>`,
    };
  }

  constructor({ data, api }) {
    this.api = api;
    this.data = {
      text: data.text || "",
      alignment: data.alignment || "left",
    };
  }

  render() {
    const wrapper = document.createElement("div");
    wrapper.style.textAlign = this.data.alignment;

    const paragraph = document.createElement("p");
    paragraph.innerHTML = this.data.text;
    paragraph.contentEditable = true;
    paragraph.style.cssText = `
      margin: 0;
      padding: 8px;
      outline: none;
      min-height: 20px;
    `;
    paragraph.setAttribute("data-placeholder", "Enter text...");

    paragraph.addEventListener("input", () => {
      this.data.text = paragraph.innerHTML;
    });

    wrapper.appendChild(paragraph);
    return wrapper;
  }

  renderSettings() {
    const wrapper = document.createElement("div");
    wrapper.style.cssText = "display: flex; gap: 4px;";

    const alignments = [
      { value: "left", icon: "⬅️" },
      { value: "center", icon: "↔️" },
      { value: "right", icon: "➡️" },
      { value: "justify", icon: "▦" },
    ];

    alignments.forEach((align) => {
      const btn = document.createElement("button");
      btn.innerHTML = align.icon;
      btn.style.cssText = `
        padding: 6px 8px;
        border: 1px solid ${
          this.data.alignment === align.value ? "#007bff" : "#ddd"
        };
        background: ${this.data.alignment === align.value ? "#007bff" : "#fff"};
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
      `;
      btn.addEventListener("click", () => {
        this.data.alignment = align.value;
        wrapper.parentElement.previousElementSibling.style.textAlign =
          align.value;
      });
      wrapper.appendChild(btn);
    });

    return wrapper;
  }

  save() {
    return {
      text: this.data.text,
      alignment: this.data.alignment,
    };
  }
}

// City Selector Component
export const CitySelector = ({ cities, selectedCity, onCityChange, loading }) => (
  <div style={{ marginBottom: "20px" }}>
    <label
      style={{
        display: "block",
        marginBottom: "8px",
        fontWeight: "bold",
        color: "#333",
      }}
    >
      Select City:
    </label>
    <select
      value={selectedCity || ""}
      onChange={(e) => onCityChange(e.target.value)}
      disabled={loading}
      style={{
        width: "100%",
        padding: "12px 16px",
        fontSize: "16px",
        borderRadius: "8px",
        border: "2px solid #e1e5e9",
        backgroundColor: loading ? "#f8f9fa" : "white",
        cursor: loading ? "not-allowed" : "pointer",
        transition: "border-color 0.3s",
      }}
    >
      <option value="">Choose a city to edit...</option>
      {cities.map((city) => (
        <option key={city.value} value={city.value}>
          {city.label}
        </option>
      ))}
    </select>
  </div>
);

// Action Buttons Component
export const ActionButtons = ({ onExport, onSave, selectedCity, loading }) => (
  <div
    style={{
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
      flexWrap: "wrap",
    }}
  >
    <button
      onClick={onExport}
      disabled={loading}
      style={{
        padding: "12px 24px",
        backgroundColor: "#28a745",
        color: "white",
        border: "none",
        borderRadius: "8px",
        cursor: loading ? "not-allowed" : "pointer",
        fontSize: "16px",
        fontWeight: "600",
        opacity: loading ? 0.6 : 1,
        transition: "all 0.3s",
        boxShadow: "0 2px 4px rgba(40, 167, 69, 0.3)",
      }}
      onMouseOver={(e) =>
        !loading && (e.target.style.backgroundColor = "#218838")
      }
      onMouseOut={(e) =>
        !loading && (e.target.style.backgroundColor = "#28a745")
      }
    >
      Export HTML
    </button>

    <button
      onClick={onSave}
      disabled={!selectedCity || loading}
      style={{
        padding: "12px 24px",
        backgroundColor: selectedCity && !loading ? "#007bff" : "#6c757d",
        color: "white",
        border: "none",
        borderRadius: "8px",
        cursor: selectedCity && !loading ? "pointer" : "not-allowed",
        fontSize: "16px",
        fontWeight: "600",
        transition: "all 0.3s",
        boxShadow:
          selectedCity && !loading
            ? "0 2px 4px rgba(0, 123, 255, 0.3)"
            : "none",
      }}
      onMouseOver={(e) =>
        selectedCity && !loading && (e.target.style.backgroundColor = "#0056b3")
      }
      onMouseOut={(e) =>
        selectedCity && !loading && (e.target.style.backgroundColor = "#007bff")
      }
    >
      Save Content
    </button>
  </div>
);

export const exportToHTML = async (editor, selectedCity, cities) => {
  if (!editor.current) return;

  try {
    const output = await editor.current.save();
    const cityName = selectedCity
      ? cities.find((c) => c.value === selectedCity)?.label
      : "City";

    let html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${cityName} Content</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 20px; }
    h1 { font-size: 2.5em; margin: 0.5em 0; }
    h2 { font-size: 2em; margin: 0.5em 0; }
    h3 { font-size: 1.5em; margin: 0.5em 0; }
    img { max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
    blockquote { border-left: 4px solid #007bff; padding-left: 16px; margin: 16px 0; font-style: italic; }
    table { width: 100%; border-collapse: collapse; margin: 16px 0; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #f8f9fa; font-weight: bold; }
    .delimiter { text-align: center; margin: 32px 0; font-size: 24px; }
  </style>
</head>
<body>
`;

    output.blocks.forEach((block) => {
      switch (block.type) {
        case "header":
          html += `  <h${block.data.level}>${block.data.text}</h${block.data.level}>\n`;
          break;
        case "paragraph":
          html += `  <p>${block.data.text}</p>\n`;
          break;
        case "list":
          const tag = block.data.style === "ordered" ? "ol" : "ul";
          html += `  <${tag}>\n`;
          block.data.items.forEach((item) => {
            html += `    <li>${item}</li>\n`;
          });
          html += `  </${tag}>\n`;
          break;
        case "quote":
          html += `  <blockquote>\n    <p>${block.data.text}</p>\n`;
          if (block.data.caption) {
            html += `    <footer>— ${block.data.caption}</footer>\n`;
          }
          html += `  </blockquote>\n`;
          break;
        case "image":
          const imgStyle = `text-align: ${block.data.alignment || "center"}`;
          html += `  <div style="${imgStyle}">\n`;
          html += `    <img src="${block.data.url}" alt="${
            block.data.caption || ""
          }" style="width: ${block.data.width || 100}%;">\n`;
          if (block.data.caption) {
            html += `    <p style="font-size: 14px; color: #666; font-style: italic; margin-top: 8px;">${block.data.caption}</p>\n`;
          }
          html += `  </div>\n`;
          break;
        case "delimiter":
          html += `  <div class="delimiter">* * *</div>\n`;
          break;
        case "table":
          html += `  <table>\n`;
          block.data.content.forEach((row, index) => {
            const tag = index === 0 ? "th" : "td";
            html += `    <tr>\n`;
            row.forEach((cell) => {
              html += `      <${tag}>${cell}</${tag}>\n`;
            });
            html += `    </tr>\n`;
          });
          html += `  </table>\n`;
          break;
      }
    });

    html += "</body>\n</html>";

    // Download HTML file
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${cityName.replace(/\s+/g, "-").toLowerCase()}-content.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    alert("HTML file exported successfully!");
  } catch (error) {
    console.error("Export failed:", error);
    alert("Export failed. Please try again.");
  }
};

export const HowToFindEditor = ({ data, onChange }) => {
  return (
    <div style={{ marginBottom: "20px", padding: "15px", border: "1px solid #e1e5e9", borderRadius: "8px" }}>
      <h3>How to Find Section</h3>
      <div style={{ marginBottom: "10px" }}>
        <label>Heading:</label>
        <input
          type="text"
          value={data.heading || ""}
          onChange={(e) => onChange({ ...data, heading: e.target.value })}
          style={{ width: "100%", padding: "8px", marginTop: "5px" }}
        />
      </div>
      <div>
        <label>Description:</label>
        <textarea
          value={data.description || ""}
          onChange={(e) => onChange({ ...data, description: e.target.value })}
          style={{ width: "100%", padding: "8px", marginTop: "5px", minHeight: "100px" }}
        />
      </div>
    </div>
  );
};

export const ReasonsToBookEditor = ({ data, onChange }) => {
  const handlePointChange = (index, value) => {
    const newPoints = [...data.points];
    newPoints[index] = value;
    onChange({ ...data, points: newPoints });
  };

  const addPoint = () => {
    onChange({ ...data, points: [...data.points, ""] });
  };

  const removePoint = (index) => {
    const newPoints = [...data.points];
    newPoints.splice(index, 1);
    onChange({ ...data, points: newPoints });
  };

  return (
    <div style={{ marginBottom: "20px", padding: "15px", border: "1px solid #e1e5e9", borderRadius: "8px" }}>
      <h3>Reasons to Book Section</h3>
      <div style={{ marginBottom: "10px" }}>
        <label>Heading:</label>
        <input
          type="text"
          value={data.heading || ""}
          onChange={(e) => onChange({ ...data, heading: e.target.value })}
          style={{ width: "100%", padding: "8px", marginTop: "5px" }}
        />
      </div>
      <div>
        <label>Points:</label>
        {data.points.map((point, index) => (
          <div key={index} style={{ display: "flex", alignItems: "center", marginBottom: "5px" }}>
            <input
              type="text"
              value={point}
              onChange={(e) => handlePointChange(index, e.target.value)}
              style={{ flex: 1, padding: "8px", marginRight: "5px" }}
            />
            <button onClick={() => removePoint(index)} style={{ padding: "5px 10px" }}>
              Remove
            </button>
          </div>
        ))}
        <button onClick={addPoint} style={{ marginTop: "10px", padding: "5px 10px" }}>
          Add Point
        </button>
      </div>
    </div>
  );
};

export const FAQEditor = ({ data, onChange }) => {
  console.log("FAQEditor data:", data);

  if (!data.questions) {
    data.questions = [];
  }
  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...data.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    onChange({ ...data, questions: newQuestions });
  };

  const addQuestion = () => {
    onChange({ ...data, questions: [...data.questions, { q: "", a: "" }] });
  };

  const removeQuestion = (index) => {
    const newQuestions = [...data.questions];
    newQuestions.splice(index, 1);
    onChange({ ...data, questions: newQuestions });
  };

  return (
    <div style={{ marginBottom: "20px", padding: "15px", border: "1px solid #e1e5e9", borderRadius: "8px" }}>
      <h3>FAQs Section</h3>
      <div style={{ marginBottom: "10px" }}>
        <label>Heading:</label>
        <input
          type="text"
          value={data.heading || ""}
          onChange={(e) => onChange({ ...data, heading: e.target.value })}
          style={{ width: "100%", padding: "8px", marginTop: "5px" }}
        />
      </div>
      <div>
        <label>Questions & Answers:</label>
        {data.questions.map((question, index) => (
          <div key={index} style={{ marginBottom: "15px", padding: "10px", border: "1px solid #eee", borderRadius: "5px" }}>
            <div style={{ marginBottom: "5px" }}>
              <label>Question:</label>
              <input
                type="text"
                value={question.q}
                onChange={(e) => handleQuestionChange(index, "q", e.target.value)}
                style={{ width: "100%", padding: "8px", marginTop: "5px" }}
              />
            </div>
            <div style={{ marginBottom: "5px" }}>
              <label>Answer:</label>
              <textarea
                value={question.a}
                onChange={(e) => handleQuestionChange(index, "a", e.target.value)}
                style={{ width: "100%", padding: "8px", marginTop: "5px", minHeight: "60px" }}
              />
            </div>
            <button onClick={() => removeQuestion(index)} style={{ padding: "5px 10px" }}>
              Remove Question
            </button>
          </div>
        ))}
        <button onClick={addQuestion} style={{ marginTop: "10px", padding: "5px 10px" }}>
          Add Question
        </button>
      </div>
    </div>
  );
};
